import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Typed text primitive. Every string in a redesigned screen goes through this
 * so the type scale lives in one file and can't drift page to page.
 *
 * The scale has two voices, matching the reference:
 *   display (Gilroy) — page titles, numbers, anything you read at a glance
 *   body (Manrope)   — labels, values, prose, anything you read a line of
 *
 * `fieldLabel` is the one to reach for in detail panels: the tiny grey caption
 * that sits above a bold value.
 */

export type TextVariant =
  | 'display' | 'h1' | 'h2' | 'h3' | 'title'
  | 'stat' | 'statSm'
  | 'body' | 'bodyMedium' | 'bodySemi'
  | 'label' | 'labelSemi' | 'fieldLabel' | 'caption' | 'micro'
  | 'eyebrow';

export type TextTone =
  | 'primary' | 'secondary' | 'tertiary'
  | 'brand' | 'green' | 'onDark' | 'onDarkMuted'
  | 'success' | 'danger' | 'warning' | 'info' | 'inherit';

const VARIANTS: Record<TextVariant, string> = {
  display: 'font-display font-bold text-[28px] leading-8 tracking-[-0.6px] md:text-[34px] md:leading-10 md:tracking-[-1px]',
  h1: 'font-display font-bold text-[24px] leading-8 tracking-[-0.5px] md:text-[28px] md:leading-9',
  h2: 'font-display font-bold text-[19px] leading-7 tracking-[-0.3px]',
  h3: 'font-display font-semibold text-[15px] leading-[22px] tracking-[-0.1px]',
  title: 'font-display font-semibold text-sm leading-5',

  // The big number on a stat tile, and its smaller sibling in a dense row.
  stat: 'font-display font-bold text-[24px] leading-8 tracking-[-0.5px] nums',
  statSm: 'font-display font-bold text-lg leading-6 tracking-[-0.3px] nums',

  body: 'font-body font-normal text-sm leading-5',
  bodyMedium: 'font-body font-medium text-sm leading-5',
  bodySemi: 'font-body font-semibold text-sm leading-5',

  label: 'font-body font-medium text-[13px] leading-[18px]',
  labelSemi: 'font-body font-semibold text-[13px] leading-[18px]',
  /** Tiny grey caption above a value, as in the reference's detail panel. */
  fieldLabel: 'font-body font-medium text-[11px] leading-4',
  caption: 'font-body font-medium text-xs leading-4',
  micro: 'font-body font-semibold text-[11px] leading-[14px]',
  /** Sidebar group headings — MARKETING, PAYMENTS, SYSTEM. */
  eyebrow: 'font-body font-semibold text-[10px] leading-4 tracking-[0.09em] uppercase',
};

const TONES: Record<TextTone, string> = {
  primary: 'text-flup-ink',
  secondary: 'text-flup-ink-2',
  tertiary: 'text-flup-ink-3',
  brand: 'text-flup-brand',
  green: 'text-flup-green',
  onDark: 'text-white',
  onDarkMuted: 'text-white/60',
  success: 'text-flup-success',
  danger: 'text-flup-danger',
  warning: 'text-flup-warning',
  info: 'text-flup-info',
  /** Take the colour from the parent — for text inside a coloured control. */
  inherit: 'text-current',
};

const DEFAULT_TAG: Partial<Record<TextVariant, keyof JSX.IntrinsicElements>> = {
  display: 'h1',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  body: 'p',
};

const CLAMP: Record<number, string> = {
  1: 'truncate',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
};

export interface TextProps extends React.HTMLAttributes<HTMLElement> {
  variant?: TextVariant;
  tone?: TextTone;
  as?: keyof JSX.IntrinsicElements;
  /** Truncate to N lines. 1 uses ellipsis; >1 uses line-clamp. */
  lines?: number;
  children?: React.ReactNode;
}

const Text = React.forwardRef<HTMLElement, TextProps>(
  ({ variant = 'body', tone = 'primary', as, lines, className, children, ...rest }, ref) => {
    const Tag = (as || DEFAULT_TAG[variant] || 'span') as any;

    return (
      <Tag
        ref={ref}
        className={cn(
          VARIANTS[variant] ?? VARIANTS.body,
          TONES[tone] ?? TONES.primary,
          lines ? CLAMP[lines] ?? 'line-clamp-6' : undefined,
          className,
        )}
        {...rest}
      >
        {children}
      </Tag>
    );
  },
);

Text.displayName = 'FlupText';

export default React.memo(Text);
